// Pure (React-free) parse/serialize/validate helpers for URL query state.
//
// These are the risk-bearing bits of the URL-state hooks — the strict
// validation and clean-URL rules — kept pure so they can be unit-tested
// without a router or a rendered component.
//
//   raw string (from ?key=…)  ──parse──▶  typed value (validated)
//   typed value               ──isDefault?──▶  drop the param (clean URL)
//
// Every parser falls back to a caller-supplied default when the raw value is
// absent or fails validation, so a hand-edited/garbage URL can never push an
// invalid value into an API call.

/**
 * Parse a single string value, optionally constrained to an allow-list.
 * Unknown / absent values fall back to `fallback`.
 */
export function parseEnum<T extends string>(
  raw: string | null,
  fallback: T,
  allowed?: readonly T[]
): T {
  if (raw == null) return fallback;
  if (allowed && !allowed.includes(raw as T)) return fallback;
  return raw as T;
}

/**
 * Parse an integer, clamped to a minimum. Non-numeric, empty, or
 * below-minimum values fall back to `fallback`.
 */
export function parseNumber(
  raw: string | null,
  fallback: number,
  min = Number.NEGATIVE_INFINITY
): number {
  if (raw == null || raw.trim() === '') return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return fallback;
  if (n < min) return fallback;
  return n;
}

/**
 * Parse a comma-separated list. When `allowed` is supplied, unknown members
 * are dropped (so `?status=CREATED,BOGUS` → `['CREATED']`). Blank segments
 * and duplicates are removed. Absent → empty array.
 */
export function parseArray<T extends string>(
  raw: string | null,
  allowed?: readonly T[]
): T[] {
  if (raw == null || raw.trim() === '') return [];
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const filtered = allowed
    ? parts.filter((p): p is T => allowed.includes(p as T))
    : (parts as T[]);
  // de-dupe, preserve first-seen order
  return Array.from(new Set(filtered));
}

/** Serialize a list back to the `a,b,c` URL form. */
export function serializeArray(values: readonly string[]): string {
  return values.join(',');
}

/** Order-insensitive set equality — used to decide "is this the default?". */
export function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((x) => setB.has(x));
}
